import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import AzureAuthButton from '../components/auth/AzureAuthButton';
import { checkFirebaseFunction } from '../utils/authUtils';
const LOGO_URL = '/logotranscription.png';

const LoginPage = () => {
  const { isAuthenticated, error: authError, authStatus } = useAuth();
  const { addSystemNotification } = useNotifications();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [functionStatus, setFunctionStatus] = useState({
    isLoading: true,
    isAccessible: false,
    status: 0,
    message: 'Vérification en cours...'
  });
  
  // Vérifier si la fonction Cloud Firebase est accessible
  // useEffect(() => {
  //   const checkCloudFunction = async () => {
  //     try {
  //       setFunctionStatus(prevState => ({ ...prevState, isLoading: true }));
        
  //       const result = await checkFirebaseFunction();
        
  //       setFunctionStatus({
  //         isLoading: false,
  //         isAccessible: result.isAccessible,
  //         status: result.status,
  //         message: result.message
  //       });
  //     } catch (error) {
  //       addSystemNotification('❌ Erreur lors de la vérification de la fonction Cloud:', 'error');
  //       setFunctionStatus({
  //         isLoading: false,
  //         isAccessible: false,
  //         status: 0,
  //         message: `Erreur: ${error.message}`
  //       });
  //     }
  //   };
    
  //   checkCloudFunction();
  // }, []);
  
  // Redirection si déjà connecté - SIMPLIFIÉE pour éviter les boucles
  useEffect(() => {
    // Si authentifié dans le contexte, rediriger vers la page d'accueil
    if (isAuthenticated) {
      navigate('/');
    }
    
    // Supprimer la vérification localStorage qui causait des rechargements
    // L'AuthContext se charge déjà de la synchronisation avec localStorage
  }, [isAuthenticated, navigate]);
  
  // Créer un objet de débogage formaté
  const renderDebugInfo = () => {
    // Récupérer les comptes MSAL actuels
    const msalAccounts = window.msal ? window.msal.getAllAccounts() : [];
    
    return (
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2">Informations de débogage</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="div">
            <Box component="pre" sx={{ 
              backgroundColor: '#f5f5f5', 
              p: 1, 
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.75rem'
            }}>
              {JSON.stringify({ 
                authStatus, 
                isAuthenticated,
                localStorage: {
                  testUser: Boolean(localStorage.getItem('testUser')),
                  azureTestUser: Boolean(localStorage.getItem('azureTestUser')),
                  redirectInProgress: Boolean(localStorage.getItem('redirectInProgress'))
                },
                sessionStorage: {
                  msalActiveAccount: Boolean(sessionStorage.getItem('msalActiveAccount'))
                },
                msalAccounts: msalAccounts.length,
                functionStatus
              }, null, 2)}
            </Box>
          </Typography>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Button 
                variant="outlined" 
                color="error" 
                size="small" 
                fullWidth
                onClick={() => {
                  localStorage.removeItem('testUser');
                  localStorage.removeItem('azureTestUser');
                  localStorage.removeItem('redirectInProgress');
                  sessionStorage.removeItem('msalActiveAccount');
                  window.location.reload();
                }}
              >
                Effacer les caches et recharger
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button 
                variant="outlined" 
                color="warning" 
                size="small"
                fullWidth
                onClick={() => {
                  // Forcer le nettoyage complet des caches MSAL
                  if (window.msal) {
                    const accounts = window.msal.getAllAccounts();
                    if (accounts.length > 0) {
                      window.msal.logoutPopup({ account: accounts[0] })
                        .then(() => {
                          // Nettoyage manuel après déconnexion
                          localStorage.clear();
                          sessionStorage.clear();
                          window.location.href = '/login';
                        })
                        .catch(e => {
                          addSystemNotification('Erreur lors de la déconnexion:', 'error');
                          // Nettoyage de secours
                          localStorage.clear();
                          sessionStorage.clear();
                          window.location.href = '/login';
                        });
                    } else {
                      // Pas de compte connecté, nettoyer quand même
                      localStorage.clear();
                      sessionStorage.clear();
                      window.location.href = '/login';
                    }
                  } else {
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                  }
                }}
              >
                Nettoyage complet et déconnexion
              </Button>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 1, 
                border: '1px solid', 
                borderColor: functionStatus.isAccessible ? 'success.main' : 'error.main',
                borderRadius: 1,
                bgcolor: functionStatus.isAccessible ? 'success.light' : 'error.light',
                opacity: 0.8
              }}>
                {functionStatus.isLoading ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : functionStatus.isAccessible ? (
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                ) : (
                  <ErrorIcon color="error" sx={{ mr: 1 }} />
                )}
                <Typography variant="body2">
                  <strong>Fonction Cloud Firebase:</strong> {functionStatus.message}
                </Typography>
                {!functionStatus.isLoading && (
                  <Button 
                    size="small" 
                    sx={{ ml: 'auto' }}
                    onClick={() => {
                      const checkCloudFunction = async () => {
                        try {
                          setFunctionStatus(prevState => ({ ...prevState, isLoading: true }));
                          const result = await checkFirebaseFunction();
                          setFunctionStatus({
                            isLoading: false,
                            isAccessible: result.isAccessible,
                            status: result.status,
                            message: result.message
                          });
                        } catch (error) {
                          setFunctionStatus({
                            isLoading: false,
                            isAccessible: false,
                            status: 0,
                            message: `Erreur: ${error.message}`
                          });
                        }
                      };
                      checkCloudFunction();
                    }}
                  >
                    Vérifier
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };
  
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4
        }}
      >
        <Card elevation={3} sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box 
                component="img" 
                src={LOGO_URL} 
                alt="Logo Transcription" 
                sx={{ height: 80, width: 'auto', mb: 2 }} 
              />
              <Typography variant="h5" component="h1" gutterBottom>
                Connexion
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Transcription et résumé de réunions
              </Typography>
            </Box>
            
            {/* Message d'erreur */}
            {(error || authError) && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error || authError}
              </Alert>
            )}
            
            {/* Connexion avec Microsoft */}
            <Box sx={{ mb: 3 }}>
              <AzureAuthButton 
                fullWidth 
                onSuccess={() => {
                  navigate('/');
                }} 
              />
            </Box>
            
            {/* Informations de débogage */}
            {/* {renderDebugInfo()} */}
          </CardContent>
        </Card>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          © {new Date().getFullYear()} Matthieu SIMAR. Tous droits réservés.
        </Typography>
      </Box>
    </Container>
  );
};

export default LoginPage; 